import { Component, Input, Output, EventEmitter, Inject, OnChanges, SimpleChanges, HostListener, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminService, Team, Employee, TeamConsistencyIssue } from '../../../core/services/admin.service';
import { TeamMember } from '../../../models/team.model';
import { AdminFeedbackService } from '../../../core/services/admin-feedback.service';
import { DialogService } from '../../../core/services/dialog.service';

type TeamEditModel = {
  description?: string;
  memberships: TeamMemberEdit[];
};

type TeamMemberEdit = {
  employee_id: number;
  boss_employee_id: number;
};

@Component({
  selector: 'app-admin-team-tab',
  standalone: false,
  template: require('./admin-team-tab.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./admin-team-tab.component.scss').default],
})
export class AdminTeamTabComponent implements OnChanges {
  private readonly requiredCreateFields: Array<'description' | 'memberships'> = ['description', 'memberships'];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.btn-group')) {
      this.showMoreMenu = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showMoreMenu = false;
  }

  @Input() teams: Team[] = [];
  @Input() employees: Employee[] = [];
  @Output() dataUpdated = new EventEmitter<void>();

  currentIndex = 0;
  selectedTeam: Team | null = null;
  editTeam: TeamEditModel = { memberships: [] };
  initialEditTeam: TeamEditModel = { memberships: [] };
  originalId: number | null = null;

  selectedAvailableMember: Employee | null = null;
  selectedAvailableBoss: Employee | null = null;
  selectableEmployees: Employee[] = [];
  selectableBosses: Employee[] = [];
  availableMembersToAdd: Employee[] = [];
  memberRows: TeamMemberEdit[] = [];

  isNew = false;
  isDirty = false;
  showMoreMenu = false;
  consistencyLoading = false;
  showConsistencyDetails = false;
  consistencyIssues: TeamConsistencyIssue[] = [];
  private pendingSelectionId: number | null = null;
  private pendingSelectionIndex: number | null = null;
  private hasLoadedConsistency = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(AdminFeedbackService) private adminFeedback: AdminFeedbackService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['teams'] || changes['employees']) {
      if (this.isNew || this.isDirty) {
        if (!this.isNew && this.selectedTeam) {
          const selectedIndex = this.teams.findIndex((team) => team.team_id === this.selectedTeam?.team_id);
          this.currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
        } else if (this.teams.length === 0) {
          this.currentIndex = 0;
        } else {
          this.currentIndex = Math.min(this.currentIndex, this.teams.length - 1);
        }

        this.pendingSelectionId = null;
        this.pendingSelectionIndex = null;
        this.rebuildSelectableData();

        if ((changes['teams'] || changes['employees']) && !this.consistencyLoading) {
          this.refreshConsistency();
        }
        return;
      }

      if (this.teams.length > 0) {
        const preferredId = this.pendingSelectionId ?? this.originalId ?? this.selectedTeam?.team_id ?? null;
        const preferredIndex = preferredId === null ? -1 : this.teams.findIndex((team) => team.team_id === preferredId);

        if (preferredIndex >= 0) {
          this.currentIndex = preferredIndex;
          this.selectTeam(this.teams[preferredIndex]);
        } else if (this.pendingSelectionIndex !== null) {
          const clampedIndex = Math.min(this.pendingSelectionIndex, this.teams.length - 1);
          this.currentIndex = clampedIndex;
          this.selectTeam(this.teams[clampedIndex]);
        } else {
          this.currentIndex = 0;
          this.selectTeam(this.teams[0]);
        }
      } else {
        this.selectedTeam = null;
        this.editTeam = { memberships: [] };
        this.initialEditTeam = { memberships: [] };
        this.currentIndex = 0;
      }

      this.pendingSelectionId = null;
      this.pendingSelectionIndex = null;
      this.rebuildSelectableData();

      if ((changes['teams'] || changes['employees']) && !this.consistencyLoading) {
        this.refreshConsistency();
      }
    }
  }

  get hasConsistencyIssues(): boolean {
    return this.consistencyIssues.length > 0;
  }

  get sortedConsistencyIssues(): TeamConsistencyIssue[] {
    return [...this.consistencyIssues].sort((left, right) => {
      const descriptionCompare = (left.description || '').localeCompare(right.description || '', 'sv');
      if (descriptionCompare !== 0) return descriptionCompare;
      const employeeCompare = (left.employee_name || '').localeCompare(right.employee_name || '', 'sv');
      if (employeeCompare !== 0) return employeeCompare;
      return left.type.localeCompare(right.type, 'sv');
    });
  }

  get memberCount(): number {
    return this.editTeam.memberships.length;
  }

  refreshConsistency(): void {
    this.consistencyLoading = true;
    this.adminService.getTeamConsistency().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.consistencyIssues = result.issues || [];
        this.hasLoadedConsistency = true;
        if (!this.hasConsistencyIssues) {
          this.showConsistencyDetails = false;
        }
        this.consistencyLoading = false;
      },
      error: (err) => {
        console.error('Error loading team consistency', err);
        this.consistencyIssues = [];
        this.hasLoadedConsistency = true;
        this.consistencyLoading = false;
      },
    });
  }

  toggleConsistencyDetails(): void {
    this.showConsistencyDetails = !this.showConsistencyDetails;
  }

  getTeamOptionLabel(team: Team | null | undefined): string {
    return team?.description || '';
  }

  getEmployeeName(employeeId: number | undefined): string {
    const employee = this.employees.find((item) => item.employee_id === employeeId);
    return employee?.name || '';
  }

  selectTeam(team: Team): void {
    this.selectedTeam = team;
    this.originalId = team.team_id;
    this.editTeam = {
      description: team.description,
      memberships: team.members.map((member) => ({
        employee_id: member.employee_id,
        boss_employee_id: member.boss_employee_id,
      })),
    };
    this.initialEditTeam = {
      description: team.description,
      memberships: team.members.map((member) => ({
        employee_id: member.employee_id,
        boss_employee_id: member.boss_employee_id,
      })),
    };
    this.selectedAvailableMember = null;
    this.selectedAvailableBoss = null;
    this.isNew = false;
    this.isDirty = false;
    this.rebuildSelectableData();
  }

  onTeamSelect(): void {
    if (this.selectedTeam) {
      const index = this.teams.findIndex((team) => team.team_id === this.selectedTeam?.team_id);
      if (index >= 0) {
        this.currentIndex = index;
      }
      this.selectTeam(this.selectedTeam);
    }
  }

  movePrev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.selectTeam(this.teams[this.currentIndex]);
    }
  }

  moveNext(): void {
    if (this.currentIndex < this.teams.length - 1) {
      this.currentIndex++;
      this.selectTeam(this.teams[this.currentIndex]);
    }
  }

  addNew(): void {
    this.isNew = true;
    this.selectedTeam = null;
    this.originalId = null;
    this.selectedAvailableMember = null;
    this.selectedAvailableBoss = null;
    this.editTeam = {
      description: '',
      memberships: [],
    };
    this.initialEditTeam = { description: '', memberships: [] };
    this.isDirty = false;
    this.rebuildSelectableData();
  }

  save(): void {
    const payload = {
      description: this.editTeam.description,
      memberships: this.editTeam.memberships.map((member) => ({
        employee_id: member.employee_id,
        boss_employee_id: member.boss_employee_id,
      })),
    };

    if (this.isNew) {
      this.adminService.createTeam(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result) => {
          this.pendingSelectionId = result?.team_id ?? null;
          this.isNew = false;
          this.isDirty = false;
          this.refreshConsistency();
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('skapa', 'team', err),
      });
    } else if (this.originalId !== null) {
      this.adminService.updateTeam(this.originalId, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result) => {
          this.pendingSelectionId = result?.team_id ?? this.originalId;
          this.isDirty = false;
          this.refreshConsistency();
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('uppdatera', 'team', err),
      });
    }
  }

  cancel(): void {
    if (this.isNew) {
      this.isNew = false;
      if (this.teams.length > 0) {
        this.selectTeam(this.teams[this.currentIndex]);
      } else {
        this.editTeam = { memberships: [] };
        this.initialEditTeam = { memberships: [] };
        this.selectedAvailableMember = null;
        this.selectedAvailableBoss = null;
      }
    } else if (this.selectedTeam) {
      this.selectTeam(this.selectedTeam);
      return;
    }
    this.isDirty = false;
    this.rebuildSelectableData();
  }

  async delete(): Promise<void> {
    if (this.originalId === null) {
      return;
    }

    const confirmed = await this.dialogService.confirm({
      title: 'Bekräfta borttagning',
      body: 'Är du säker på att du vill ta bort detta team?',
      confirmText: 'Ta bort',
      cancelText: 'Avbryt',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.pendingSelectionIndex = this.currentIndex;
    this.adminService.deleteTeam(this.originalId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.refreshConsistency();
        this.dataUpdated.emit();
      },
      error: (err) => this.adminFeedback.showCrudError('ta bort', 'team', err),
    });
  }

  duplicate(): void {
    if (!this.selectedTeam) return;
    this.isNew = true;
    this.originalId = null;
    this.selectedAvailableMember = null;
    this.selectedAvailableBoss = null;
    this.editTeam = {
      description: `${this.selectedTeam.description} (kopia)`,
      memberships: this.selectedTeam.members.map((member) => ({
        employee_id: member.employee_id,
        boss_employee_id: member.boss_employee_id,
      })),
    };
    this.initialEditTeam = {
      description: this.selectedTeam.description,
      memberships: this.selectedTeam.members.map((member) => ({
        employee_id: member.employee_id,
        boss_employee_id: member.boss_employee_id,
      })),
    };
    this.isDirty = true;
    this.rebuildSelectableData();
  }

  addSelectedMember(): void {
    if (!this.selectedAvailableMember || !this.selectedAvailableBoss) return;

    if (!this.editTeam.memberships.some((member) => member.employee_id === this.selectedAvailableMember?.employee_id)) {
      this.editTeam.memberships = [
        ...this.editTeam.memberships,
        {
          employee_id: this.selectedAvailableMember.employee_id,
          boss_employee_id: this.selectedAvailableBoss.employee_id,
        },
      ];
      this.onFieldChange();
    }

    this.selectedAvailableMember = null;
    this.selectedAvailableBoss = null;
    this.rebuildSelectableData();
  }

  removeMember(employeeId: number): void {
    this.editTeam.memberships = this.editTeam.memberships.filter((member) => member.employee_id !== employeeId);
    this.onFieldChange();
    this.rebuildSelectableData();
  }

  updateMemberBoss(employeeId: number, boss: Employee | null): void {
    this.editTeam.memberships = this.editTeam.memberships.map((member) =>
      member.employee_id === employeeId
        ? { ...member, boss_employee_id: boss?.employee_id || member.boss_employee_id }
        : member
    );
    this.onFieldChange();
    this.rebuildSelectableData();
  }

  getSelectedBossForMember(member: TeamMemberEdit): Employee | null {
    return this.selectableBosses.find((employee) => employee.employee_id === member.boss_employee_id) || null;
  }

  onFieldChange(): void {
    this.isDirty = this.hasChanges();
  }

  isRequiredField(field: 'description' | 'memberships'): boolean {
    return this.requiredCreateFields.includes(field);
  }

  isRequiredMissing(field: 'description' | 'memberships'): boolean {
    if (!this.isNew || !this.isRequiredField(field)) return false;

    if (field === 'memberships') {
      return this.editTeam.memberships.length === 0;
    }

    return !String(this.editTeam.description || '').trim();
  }

  hasMissingRequiredFields(): boolean {
    return this.requiredCreateFields.some((field) => this.isRequiredMissing(field));
  }

  isFieldDirty(field: 'description' | 'memberships'): boolean {
    if (field === 'memberships') {
      return this.normalizeMemberships(this.editTeam.memberships) !== this.normalizeMemberships(this.initialEditTeam.memberships);
    }
    return String(this.editTeam.description || '') !== String(this.initialEditTeam.description || '');
  }

  hasChanges(): boolean {
    return this.isFieldDirty('description') || this.isFieldDirty('memberships');
  }

  private normalizeMemberships(value: TeamMemberEdit[] | undefined): string {
    return [...(value || [])]
      .map((item) => `${item.employee_id}:${item.boss_employee_id}`)
      .sort()
      .join('|');
  }

  canSave(): boolean {
    if (!this.isDirty) return false;
    if (!this.isNew) return true;
    return !this.hasMissingRequiredFields();
  }

  canAddNew(): boolean {
    return !this.isNew && !this.isDirty;
  }

  canUndo(): boolean {
    return this.isDirty || this.isNew;
  }

  canOpenMoreMenu(): boolean {
    return !this.isNew && !this.isDirty && this.originalId !== null;
  }

  getConsistencyStatusText(): string {
    if (this.consistencyLoading && !this.hasLoadedConsistency) {
      return 'Kontrollerar teamdata...';
    }
    if (!this.hasConsistencyIssues) {
      return 'Teamdata är konsistent.';
    }
    return `${this.consistencyIssues.length} konsistensproblem hittades i teamdata.`;
  }

  getIssueRowKey(_index: number, issue: TeamConsistencyIssue): string {
    return [issue.type, issue.description || '', issue.employee_id || '', issue.boss_employee_id || ''].join(':');
  }

  private rebuildSelectableData(): void {
    const currentMemberIds = new Set(this.editTeam.memberships.map((member) => member.employee_id));
    const assignedElsewhere = new Set(
      this.teams
        .filter((team) => team.team_id !== this.originalId)
        .flatMap((team) => team.members.map((member) => member.employee_id))
    );

    this.selectableEmployees = this.employees
      .filter((employee) => {
        if (employee.employee_id === 0) return false;
        if (employee.stop_date) return false;
        if (String(employee.contractor || '').toUpperCase() === 'J') return false;
        return currentMemberIds.has(employee.employee_id) || !assignedElsewhere.has(employee.employee_id);
      })
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'));

    this.selectableBosses = this.employees
      .filter((employee) => {
        if (employee.employee_id === 0) return false;
        if (employee.stop_date) return false;
        return String(employee.contractor || '').toUpperCase() !== 'J';
      })
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'));

    const selectedIds = new Set(this.editTeam.memberships.map((member) => member.employee_id));
    this.availableMembersToAdd = this.selectableEmployees.filter((employee) => !selectedIds.has(employee.employee_id));
    this.memberRows = [...this.editTeam.memberships].sort((a, b) =>
      this.getEmployeeName(a.employee_id).localeCompare(this.getEmployeeName(b.employee_id), 'sv')
    );

    if (this.selectedAvailableMember) {
      this.selectedAvailableMember =
        this.availableMembersToAdd.find((employee) => employee.employee_id === this.selectedAvailableMember?.employee_id) || null;
    }
    if (this.selectedAvailableBoss) {
      this.selectedAvailableBoss =
        this.selectableBosses.find((employee) => employee.employee_id === this.selectedAvailableBoss?.employee_id) || null;
    }
  }
}
